import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DataBoundary } from '../components/common/DataBoundary';
import { useToast } from '../hooks/use-toast';
import { ShoppingBag, Mail, Search, Loader2 } from 'lucide-react';

function MyOrdersPage({ user }) {
  const { toast } = useToast();
  const [email, setEmail] = useState(user?.email || '');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: 'Netfang vantar',
        description: 'Vinsamlegast sláðu inn netfang til að sækja pantanir.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    setError(false);
    setHasSearched(true);

    try {
      const response = await fetch(`${backendUrl}/api/orders/${encodeURIComponent(email)}`);
      if (!response.ok) {
        throw new Error('Tókst ekki að sækja pantanir');
      }
      const data = await response.json();
      setOrders(Array.isArray(data) ? data : []);
      setError(false);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(true);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const retryFetch = async () => {
    if (!email) return;
    // Endurkeyra leit með núverandi netfangi
    await handleSubmit(new Event('submit'));
  };

  const formatTotal = (order) => {
    if (typeof order.total === 'number') {
      return `${order.total.toLocaleString()} kr.`;
    }
    if (order.items && Array.isArray(order.items)) {
      const sum = order.items.reduce((acc, item) => acc + (item.price || 0) * (item.quantity || 1), 0);
      return `${sum.toLocaleString()} kr.`;
    }
    return '—';
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'paid') {
      return <Badge className="bg-[#E8F4F8] text-[#0A4D8C]">Greitt</Badge>;
    }
    if (s === 'pending') {
      return <Badge className="bg-[#FFF7ED] text-[#EA580C]">Í vinnslu</Badge>;
    }
    if (s === 'cancelled') {
      return <Badge className="bg-[#FEF2F2] text-[#B91C1C]">Hætt við</Badge>;
    }
    return <Badge className="bg-[#E5E7EB] text-[#374151]">Ótilgreint</Badge>;
  };

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-3"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
            data-testid="my-orders-title"
          >
            Mínar pantanir
          </h1>
          <p className="text-sm md:text-base text-[#8B9BA8] max-w-xl mx-auto">
            Sláðu inn netfangið sem þú notaðir í vefversluninni til að skoða fyrri pantanir.
          </p>
        </div>

        <Card className="mb-8" data-testid="my-orders-search-card">
          <CardHeader>
            <CardTitle className="text-lg md:text-xl flex items-center gap-2 text-[#2C3E50]">
              <ShoppingBag className="w-5 h-5 text-[#0A4D8C]" />
              Leita að pöntunum
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-[#8B9BA8] mb-4" data-testid="my-orders-helper-text">
              Við finnum pantanir sem tengjast netfanginu sem þú notaðir í vefversluninni. Ef þú notar fleiri en eitt
              netfang, prófaðu það sem líklegast er.
            </p>
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              data-testid="my-orders-search-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 items-end">
                <div>
                  <Label htmlFor="email" className="text-[#2C3E50] flex items-center gap-1">
                    Netfang
                    <span className="text-xs text-[#8B9BA8]">(vefverslun)</span>
                  </Label>
                  <div className="relative mt-2">
                    <Mail className="w-4 h-4 text-[#8B9BA8] absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nafn@example.is"
                      className="pl-9"
                      data-testid="my-orders-email-input"
                    />
                  </div>
                </div>
                <div className="flex md:justify-end">
                  <Button
                    type="submit"
                    className="w-full md:w-auto bg-[#0A4D8C] hover:bg-[#083A6B] text-white"
                    disabled={loading}
                    data-testid="my-orders-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Leita...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Sækja pantanir
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        <DataBoundary
          isLoading={loading && hasSearched}
          isError={error && hasSearched}
          isEmpty={hasSearched && !loading && !error && orders.length === 0}
          skeleton={
            <Card data-testid="my-orders-skeleton-card">
              <CardContent className="p-6 space-y-3" data-testid="my-orders-skeleton">
                {[...Array(4)].map((_, idx) => (
                  <div
                    key={idx}
                    className="h-10 w-full skeleton-shimmer rounded-md"
                  />
                ))}
              </CardContent>
            </Card>
          }
          emptyProps={{
            icon: 'search',
            title: 'Engar pantanir fundust',
            description: 'Athugaðu netfangið eða prófaðu annað netfang sem þú gætir hafa notað.',
            'data-testid': 'empty-my-orders',
          }}
          errorProps={{
            title: 'Tókst ekki að sækja pantanir',
            description: 'Athugaðu nettengingu eða reyndu aftur síðar.',
            onRetry: retryFetch,
            'data-testid': 'error-my-orders',
          }}
          data-testid="my-orders-boundary"
        >
          <Card data-testid="my-orders-result-card">
            <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <CardTitle className="text-lg md:text-xl text-[#2C3E50] flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#0A4D8C]" />
                Pöntunarsaga fyrir
                <span className="font-semibold">{email}</span>
              </CardTitle>
              <Badge className="bg-[#E8F4F8] text-[#0A4D8C]" data-testid="my-orders-count-badge">
                {orders.length} pantanir
              </Badge>
            </CardHeader>
            <CardContent className="p-0">
              <Table data-testid="my-orders-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[#2C3E50] font-semibold">Dagsetning</TableHead>
                    <TableHead className="text-[#2C3E50] font-semibold">Pöntun nr.</TableHead>
                    <TableHead className="text-[#2C3E50] font-semibold">Greiðslustaða</TableHead>
                    <TableHead className="text-right text-[#2C3E50] font-semibold">Heildarverð</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => {
                    const id = order.order_id || order._id || order.id;
                    return (
                      <TableRow
                        key={id}
                        data-testid={`my-orders-row-${id}`}
                      >
                        <TableCell>{order.created_at || order.date || '—'}</TableCell>
                        <TableCell>{id}</TableCell>
                        <TableCell>{getStatusBadge(order.status)}</TableCell>
                        <TableCell className="text-right font-semibold text-[#0A4D8C]">
                          {formatTotal(order)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </DataBoundary>
      </div>
    </div>
  );
}

export default MyOrdersPage;
